import { FC, useState } from 'react';
import { generateThirdLevelCategoryPageLink } from 'src/utils/links';
import NextImage from 'src/shared-components/NextImage/NextImage';
import {
  FirstCategoryLevel,
  SecondCategoryLevel,
  ThirdCategoryLevel
} from 'src/api/types';
import { FormattedMessage } from 'react-intl';
import useMedia from 'src/hooks/useMedia';
import dynamic from 'next/dynamic';
import Skeleton from 'react-loading-skeleton';
import { delayWrapper } from 'src/utils/delayWrapper';
import trns from '../trns';
import Button from '../../../shared-components/design-system/ButtonTertiary';

const NavigationLink = dynamic(
  () =>
    delayWrapper<typeof NavigationLink>(
      import('src/shared-components/NavigationLink/NavigationLink')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

interface Props {
  selectedFirstLevelCategory: FirstCategoryLevel;
  selectedSecondLevelCategory: SecondCategoryLevel;
  selectedThirdLevelCategory: ThirdCategoryLevel[];
  defaultItemsDisplayed?: number;
  openModal?: () => void;
  closeModal?: () => void;
}

const arrowIcon = 'shared/arrow-forward-grey.svg';

const ThirdLevelCategoryList: FC<Props> = ({
  selectedFirstLevelCategory,
  selectedSecondLevelCategory,
  selectedThirdLevelCategory,
  defaultItemsDisplayed = 10,
  openModal,
  closeModal
}) => {
  const { lm } = useMedia();
  const [categoryList, setCategoryList] = useState(defaultItemsDisplayed);

  const handleLoadMoreClick = () => {
    if (lm) {
      setCategoryList(
        (prev) =>
          prev + selectedThirdLevelCategory?.length - defaultItemsDisplayed
      );
    } else {
      openModal();
    }
  };

  return (
    <div className="flex flex-col w-full space-y-4 px-4 my-[16px]">
      {selectedThirdLevelCategory
        ?.slice(0, categoryList)
        ?.map((categoryThirdLevel) => (
          <div
            key={categoryThirdLevel.name}
            className="pt-2 pb-2 border-b border-gallery"
          >
            <NavigationLink
              key={categoryThirdLevel.name}
              href={generateThirdLevelCategoryPageLink({
                category: selectedFirstLevelCategory,
                secondCategoryId: selectedSecondLevelCategory.translitName,
                thirdCategoryId: categoryThirdLevel.translitName
              })}
              passClassName="flex w-full justify-between items-center text-astronautBlue"
              onClick={() => closeModal()}
            >
              <div>{categoryThirdLevel.name}</div>
              <NextImage
                alt={categoryThirdLevel?.translitName}
                src={arrowIcon}
                layout="fixed"
                width={24}
                height={24}
              />
            </NavigationLink>
          </div>
        ))}
      {selectedThirdLevelCategory?.length > defaultItemsDisplayed &&
        categoryList === defaultItemsDisplayed && (
          <Button
            onClick={handleLoadMoreClick}
            buttonLabel={<FormattedMessage {...trns.showCategories} />}
          />
        )}
      {categoryList > defaultItemsDisplayed && (
        <Button
          onClick={() => setCategoryList(defaultItemsDisplayed)}
          buttonLabel={<FormattedMessage {...trns.hideCategories} />}
        />
      )}
    </div>
  );
};

export default ThirdLevelCategoryList;
